import { Logo } from "@/components/branding/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import { Lock, Mail, Phone, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

export default function ClientRegister() {
  const { tenant: tenantFromPath } = useParams<{ tenant?: string }>();
  const [searchParams] = useSearchParams();
  const tenant =
    (tenantFromPath || "").trim() || searchParams.get("tenant")?.trim() || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selfRegistrationEnabled, setSelfRegistrationEnabled] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (tenant) setClientTenant(tenant);
  }, [tenant]);

  useEffect(() => {
    if (!tenant) {
      setSelfRegistrationEnabled(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [data] = await clientAuthService.fetchTenantSettings(tenant);
      if (!cancelled) {
        setSelfRegistrationEnabled(data?.selfRegistrationEnabled ?? false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasContact = formData.email.trim() || formData.phone.trim();
    if (!formData.name.trim() || !hasContact || !formData.password) {
      toast({
        title: "Erro",
        description: "Preencha nome, e-mail ou telefone, e senha",
        variant: "destructive",
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não conferem",
        variant: "destructive",
      });
      return;
    }
    if (!tenant) {
      toast({
        title: "Erro",
        description: "Link de tenant inválido",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const [res, error] = await clientAuthService.register({
      name: formData.name.trim(),
      email: formData.email.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      password: formData.password,
      tenant,
    });
    setIsLoading(false);
    if (error) return;
    if (!res) return;

    if (res.requiresWhatsappConfirmation) {
      toast({
        title: "Quase lá!",
        description:
          res.msg || "Enviamos um link para o seu WhatsApp — clique nele para concluir o cadastro.",
      });
      return;
    }

    setClientTenant(tenant);

    if (res.accessToken && res.user) {
      toast({
        title: "Bem-vindo!",
        description: "Sua conta foi criada. Redirecionando…",
      });
      navigate(`${getClientPortalBase()}/tickets`);
      return;
    }

    if (res.pendingApproval) {
      toast({
        title: "Cadastro recebido",
        description:
          res.msg ||
          "Aguarde a aprovação da organização. Você receberá acesso ao portal quando for aprovado.",
      });
      navigate(`${getClientPortalBase()}/login?tenant=${encodeURIComponent(tenant)}`);
      return;
    }

    toast({
      title: "Sucesso",
      description: res.msg || "Cadastro realizado.",
    });
    navigate(`${getClientPortalBase()}/login?tenant=${encodeURIComponent(tenant)}`);
  };

  if (tenant && selfRegistrationEnabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-card shadow-lg rounded-lg p-8 w-full max-w-md text-center space-y-4">
          <h1 className="text-xl font-bold">Cadastro indisponível</h1>
          <p className="text-muted-foreground text-sm">
            Esta organização não permite criar conta pelo portal. Entre em contato com o suporte ou peça um
            convite.
          </p>
          <Button asChild variant="outline">
            <Link to={`${getClientPortalBase()}/login`}>Voltar ao login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card shadow-lg rounded-lg p-8 w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo size={32} textClassName="text-2xl" />
          </div>
          <h1 className="text-2xl font-bold">Cadastro de Cliente</h1>
          {tenant && (
            <p className="text-muted-foreground text-sm">
              Organização: <span className="font-medium">{tenant}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium">
              Nome
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="name"
                type="text"
                placeholder="Seu nome completo"
                className="pl-10"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium">
              E-mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                className="pl-10"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium">
              Telefone (WhatsApp)
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                placeholder="5511999999999"
                className="pl-10"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium">
              Senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                className="pl-10"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm font-medium">
              Confirmar senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repita a senha"
                className="pl-10"
                value={formData.confirmPassword}
                onChange={(e) => handleChange("confirmPassword", e.target.value)}
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || selfRegistrationEnabled === null}>
            {isLoading ? "Cadastrando..." : "Cadastrar"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Já tem conta?{" "}
          <Link to={`${getClientPortalBase()}/login`} className="text-primary hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
